import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faRobot, faSpinner, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Button } from '../ui/Button';
import { Card, CardContent } from '../ui/Card';

interface SimplificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalText: string;
  simplifiedText?: string;
  title?: string;
  isLoading: boolean;
  provider?: string;
  targetLevel?: string;
}

export default function SimplificationModal({
  isOpen,
  onClose,
  originalText,
  simplifiedText,
  title,
  isLoading,
  provider,
  targetLevel,
}: SimplificationModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faRobot} className="text-white text-xl" />
            <h2 className="text-xl font-bold text-white">
              {title || 'Simplificación con IA'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Metadata */}
        {(provider || targetLevel) && !isLoading && (
          <div className="px-6 py-3 bg-blue-50 border-b border-blue-200 flex items-center gap-4 text-sm">
            {provider && (
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-700">Proveedor:</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                  {provider === 'CHATGPT' ? 'ChatGPT' : 'Claude'}
                </span>
              </div>
            )}
            {targetLevel && (
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-700">Nivel objetivo:</span>
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded">
                  {targetLevel}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-blue-600 text-5xl mb-4 animate-spin"
              />
              <p className="text-lg font-semibold text-gray-700">Simplificando texto...</p>
              <p className="text-sm text-gray-500 mt-2">
                Esto puede tomar unos segundos
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Original Text */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-lg font-bold mb-3 text-gray-800 flex items-center gap-2">
                    Texto Original
                  </h3>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
                    <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                      {originalText}
                    </p>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    {originalText.split(/\s+/).length} palabras
                  </div>
                </CardContent>
              </Card>

              {/* Simplified Text */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-lg font-bold mb-3 text-green-700 flex items-center gap-2">
                    <FontAwesomeIcon icon={faCheck} className="text-green-600" />
                    Texto Simplificado
                  </h3>
                  {simplifiedText ? (
                    <>
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200 max-h-96 overflow-y-auto">
                        <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                          {simplifiedText}
                        </p>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        {simplifiedText.split(/\s+/).length} palabras
                      </div>
                    </>
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center text-gray-500">
                      No hay texto simplificado disponible
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end gap-3">
          {simplifiedText && !isLoading && (
            <Button
              onClick={() => {
                navigator.clipboard.writeText(simplifiedText);
                alert('Texto simplificado copiado al portapapeles');
              }}
              variant="outline"
            >
              Copiar texto simplificado
            </Button>
          )}
          <Button onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}
